import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import type DocsService from '../services/kolay/docs.ts';
import type { Collection, Page } from '../services/kolay/types.ts';
import type RouterService from '@ember/routing/router-service';
export class PageNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Selected Group".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
    };
    Blocks: {
        /**
     * If provided, this block will yield back the page for customizing the name.
     * By default the `name` property will be used.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:page as |page|>
     *       {{toSentenceCase page.name}}
     *     </:page>
     *   </PageNav>
     * </template>
     * ```
     */ page: [Page];
        /**
     * If provided, this block will yield back the collection for customizing the name.
     * By default the `name` property will be used.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:collection as |collection|>
     *       {{toSentenceCase collection.name}}
     *     </:collection>
     *   </PageNav>
     * </template>
     * ```
     */ collection: [Collection];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    /**
   * Ember doesn't yet have a way to forward blocks,
   * so we have  to do this weird manualy forwarding ourselves
   *
   * This is extra annoying since Pages is a recursive component.
   */ static{
        template(`
    <nav aria-label='Selected Group' ...attributes>
      <Pages @item={{this.docs.tree}}>

        <:page as |p|>
          {{#if (has-block 'page')}}
            {{yield p to='page'}}
          {{else}}
            {{p.name}}
          {{/if}}
        </:page>

        <:collection as |c|>
          {{#if (has-block 'collection')}}
            {{yield c to='collection'}}
          {{else}}
            {{c.name}}
          {{/if}}
        </:collection>

      </Pages>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
function isCollection(x1: Page | Collection): x is Collection {
    return 'pages' in x1;
}
let Pages = class Pages extends Component<{
    Args: {
        item: Page | Collection;
        activeClass?: string;
    };
    Blocks: {
        page: [Page];
        collection: [Collection];
    };
}> {
    @service
    router: RouterService;
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    isActive = (subPath1: string)=>{
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1);
    };
    static{
        template(`
    <ul>
      {{#if (isCollection @item)}}
        {{#each @item.pages as |page|}}
          <li>
            {{#if (isCollection page)}}
              {{yield page to='collection'}}
            {{/if}}

            <Pages @item={{page}}>
              <:page as |p|>{{yield p to='page'}}</:page>
              <:collection as |c|>{{yield c to='collection'}}</:collection>
            </Pages>
          </li>
        {{/each}}
      {{else}}
        <a
          href={{@item.path}}
          class={{if (this.isActive @item.path) this.activeClass}}
        >{{yield @item to='page'}}</a>
      {{/if}}
    </ul>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIi92ZXJjZWwvcGF0aDAvdWkvc3JjL2NvbXBvbmVudHMvcGFnZS1uYXYuZ3RzIl0sInNvdXJjZXNDb250ZW50IjpbImltcG9ydCBDb21wb25lbnQgZnJvbSAnQGdsaW1tZXIvY29tcG9uZW50JztcbmltcG9ydCB7IHNlcnZpY2UgfSBmcm9tICdAZW1iZXIvc2VydmljZSc7XG5cbmltcG9ydCB0eXBlIERvY3NTZXJ2aWNlIGZyb20gJy4uL3NlcnZpY2VzL2tvbGF5L2RvY3MudHMnO1xuaW1wb3J0IHR5cGUgeyBDb2xsZWN0aW9uLCBQYWdlIH0gZnJvbSAnLi4vc2VydmljZXMva29sYXkvdHlwZXMudHMnO1xuaW1wb3J0IHR5cGUgUm91dGVyU2VydmljZSBmcm9tICdAZW1iZXIvcm91dGluZy9yb3V0ZXItc2VydmljZSc7XG5cbmV4cG9ydCBjbGFzcyBQYWdlTmF2IGV4dGVuZHMgQ29tcG9uZW50PHtcbiAgLyoqXG4gICAqIFRoZSBgPG5hdj5gIGVsZW1lbnQuIEl0IGhhcyBhIGRlZmF1bHQgYGFyaWEtbGFiZWxgIG9mIFwiU2VsZWN0ZWQgR3JvdXBcIi5cbiAgICogTm9ybWFsbHkgYW4gYGFyaWEtbGFiZWxgIGlzIG5vdCByZXF1aXJlZCxcbiAgICogYnV0IHdoZW4gdGhlcmUgYXJlIG11bHRpcGxlIGA8bmF2PmAgZWxlbWVudHMgb24gYSBzY3JlZW4sIGl0IGlzIHJlcXVpcmVkLlxuICAgKi9cbiAgRWxlbWVudDogSFRNTEVsZW1lbnQ7XG4gIEFyZ3M6IHtcbiAgICAvKipcbiAgICAgKiBUaGUgY2xhc3MgdG8gYXBwbHkgdG8gdGhlIGA8YT5gIGVsZW1lbnQgd2hlbiBpdHMgbGluayBpcyBhY3RpdmUuXG4gICAgICpcbiAgICAgKiBEZWZhdWx0cyB0byBcImFjdGl2ZVwiXG4gICAgICovXG4gICAgYWN0aXZlQ2xhc3M_OiBzdHJpbmc7XG4gIH07XG4gIEJsb2Nrczoge1xuICAgIC8qKlxuICAgICAqIElmIHByb3ZpZGVkLCB0aGlzIGJsb2NrIHdpbGwgeWllbGQgYmFjayB0aGUgcGFnZSBmb3IgY3VzdG9taXppbmcgdGhlIG5hbWUuXG4gICAgICogQnkgZGVmYXVsdCB0aGUgYG5hbWVgIHByb3BlcnR5IHdpbGwgYmUgdXNlZC5cbiAgICAgKlxuICAgICAqIEV4YW1wbGU6XG4gICAgICogYGBgZ2pzXG4gICAgICogaW1wb3J0IHsgUGFnZU5hdiB9IGZyb20gJ2tvbGF5L2NvbXBvbmVudHMnO1xuICAgICAqXG4gICAgICogZnVuY3Rpb24gdG9TZW50ZW5jZUNhc2UobmFtZSkgeyAvKiAuLi4gKlxcLyB9XG4gICAgICpcbiAgICAgKiA8dGVtcGxhdGU-XG4gICAgICogICA8UGFnZU5hdj5cbiAgICAgKiAgICAgPDpwYWdlIGFzIHxwYWdlfD5cbiAgICAgKiAgICAgICB7e3RvU2VudGVuY2VDYXNlIHBhZ2UubmFtZX19XG4gICAgICogICAgIDwvOnBhZ2U-XG4gICAgICogICA8L1BhZ2VOYXY-XG4gICAgICogPC90ZW1wbGF0ZT5cbiAgICAgKiBgYGBcbiAgICAgKi9cbiAgICBwYWdlOiBbUGFnZV07XG4gICAgLyoqXG4gICAgICogSWYgcHJvdmlkZWQsIHRoaXMgYmxvY2sgd2lsbCB5aWVsZCBiYWNrIHRoZSBjb2xsZWN0aW9uIGZvciBjdXN0b21pemluZyB0aGUgbmFtZS5cbiAgICAgKiBCeSBkZWZhdWx0IHRoZSBgbmFtZWAgcHJvcGVydHkgd2lsbCBiZSB1c2VkLlxuICAgICAqXG4gICAgICogRXhhbXBsZTpcbiAgICAgKiBgYGBnanNcbiAgICAgKiBpbXBvcnQgeyBQYWdlTmF2IH0gZnJvbSAna29sYXkvY29tcG9uZW50cyc7XG4gICAgICpcbiAgICAgKiBmdW5jdGlvbiB0b1NlbnRlbmNlQ2FzZShuYW1lKSB7IC8qIC4uLiAqXFwvIH1cbiAgICAgKlxuICAgICAqIDx0ZW1wbGF0ZT5cbiAgICAgKiAgIDxQYWdlTmF2PlxuICAgICAqICAgICA8OmNvbGxlY3Rpb24gYXMgfGNvbGxlY3Rpb258PlxuICAgICAqICAgICAgIHt7dG9TZW50ZW5jZUNhc2UgY29sbGVjdGlvbi5uYW1lfX1cbiAgICAgKiAgICAgPC86Y29sbGVjdGlvbj5cbiAgICAgKiAgIDwvUGFnZU5hdj5cbiAgICAgKiA8L3RlbXBsYXRlPlxuICAgICAqIGBgYFxuICAgICAqL1xuICAgIGNvbGxlY3Rpb246IFtDb2xsZWN0aW9uXTtcbiAgfTtcbn0-IHtcbiAgQHNlcnZpY2UoJ2tvbGF5L2RvY3MnKSBkZWNsYXJlIGRvY3M6IERvY3NTZXJ2aWNlO1xuXG4gIC8qKlxuICAgKiBFbWJlciBkb2Vzbid0IHlldCBoYXZlIGEgd2F5IHRvIGZvcndhcmQgYmxvY2tzLFxuICAgKiBzbyB3ZSBoYXZlICB0byBkbyB0aGlzIHdlaXJkIG1hbnVhbHkgZm9yd2FyZGluZyBvdXJzZWx2ZXNcbiAgICpcbiAgICogVGhpcyBpcyBleHRyYSBhbm5veWluZyBzaW5jZSBQYWdlcyBpcyBhIHJlY3Vyc2l2ZSBjb21wb25lbnQuXG4gICAqL1xuICA8dGVtcGxhdGU-XG4gICAgPG5hdiBhcmlhLWxhYmVsPSdTZWxlY3RlZCBHcm91cCcgLi4uYXR0cmlidXRlcz5cbiAgICAgIDxQYWdlcyBAaXRlbT17e3RoaXMuZG9jcy50cmVlfX0-XG5cbiAgICAgICAgPDpwYWdlIGFzIHxwfD5cbiAgICAgICAgICB7eyNpZiAoaGFzLWJsb2NrICdwYWdlJyl9fVxuICAgICAgICAgICAge3t5aWVsZCBwIHRvPSdwYWdlJ319XG4gICAgICAgICAge3tlbHNlfX1cbiAgICAgICAgICAgIHt7cC5uYW1lfX1cbiAgICAgICAgICB7ey9pZn19XG4gICAgICAgIDwvOnBhZ2U-XG5cbiAgICAgICAgPDpjb2xsZWN0aW9uIGFzIHxjfD5cbiAgICAgICAgICB7eyNpZiAoaGFzLWJsb2NrICdjb2xsZWN0aW9uJyl9fVxuICAgICAgICAgICAge3t5aWVsZCBjIHRvPSdjb2xsZWN0aW9uJ319XG4gICAgICAgICAge3tlbHNlfX1cbiAgICAgICAgICAgIHt7Yy5uYW1lfX1cbiAgICAgICAgICB7ey9pZn19XG4gICAgICAgIDwvOmNvbGxlY3Rpb24-XG5cbiAgICAgIDwvUGFnZXM-XG4gICAgPC9uYXY-XG4gIDwvdGVtcGxhdGU-XG59XG5cbmZ1bmN0aW9uIGlzQ29sbGVjdGlvbih4OiBQYWdlIHwgQ29sbGVjdGlvbik6IHggaXMgQ29sbGVjdGlvbiB7XG4gIHJldHVybiAncGFnZXMnIGluIHg7XG59XG5cbmNsYXNzIFBhZ2VzIGV4dGVuZHMgQ29tcG9uZW50PHtcbiAgQXJnczogeyBpdGVtOiBQYWdlIHwgQ29sbGVjdGlvbjsgYWN0aXZlQ2xhc3M_OiBzdHJpbmcgfTtcbiAgQmxvY2tzOiB7XG4gICAgcGFnZTogW1BhZ2VdO1xuICAgIGNvbGxlY3Rpb246IFtDb2xsZWN0aW9uXTtcbiAgfTtcbn0-IHtcbiAgQHNlcnZpY2UgZGVjbGFyZSByb3V0ZXI6IFJvdXRlclNlcnZpY2U7XG5cbiAgZ2V0IGFjdGl2ZUNsYXNzKCkge1xuICAgIHJldHVybiB0aGlzLmFyZ3MuYWN0aXZlQ2xhc3MgPz8gJ2FjdGl2ZSc7XG4gIH1cblxuICBpc0FjdGl2ZSA9IChzdWJQYXRoOiBzdHJpbmcpID0-IHtcbiAgICBpZiAoc3ViUGF0aCA9PT0gJy8nKSByZXR1cm4gZmFsc2U7XG5cbiAgICByZXR1cm4gdGhpcy5yb3V0ZXIuY3VycmVudFVSTD8uc3RhcnRzV2l0aChzdWJQYXRoKTtcbiAgfTtcblxuICA8dGVtcGxhdGU-XG4gICAgPHVsPlxuICAgICAge3sjaWYgKGlzQ29sbGVjdGlvbiBAaXRlbSl9fVxuICAgICAgICB7eyNlYWNoIEBpdGVtLnBhZ2VzIGFzIHxwYWdlfH19XG4gICAgICAgICAgPGxpPlxuICAgICAgICAgICAge3sjaWYgKGlzQ29sbGVjdGlvbiBwYWdlKX19XG4gICAgICAgICAgICAgIHt7eWllbGQgcGFnZSB0bz0nY29sbGVjdGlvbid9fVxuICAgICAgICAgICAge3svaWZ9fVxuXG4gICAgICAgICAgICA8UGFnZXMgQGl0ZW09e3twYWdlfX0-XG4gICAgICAgICAgICAgIDw6cGFnZSBhcyB8cHw-e3t5aWVsZCBwIHRvPSdwYWdlJ319PC86cGFnZT5cbiAgICAgICAgICAgICAgPDpjb2xsZWN0aW9uIGFzIHxjfD57e3lpZWxkIGMgdG89J2NvbGxlY3Rpb24nfX08Lzpjb2xsZWN0aW9uPlxuICAgICAgICAgICAgPC9QYWdlcz5cbiAgICAgICAgICA8L2xpPlxuICAgICAgICB7ey9lYWNofX1cbiAgICAgIHt7ZWxzZX19XG4gICAgICAgIDxhXG4gICAgICAgICAgaHJlZj17e0BpdGVtLnBhdGh9fVxuICAgICAgICAgIGNsYXNzPXt7aWYgKHRoaXMuaXNBY3RpdmUgQGl0ZW0ucGF0aCkgdGhpcy5hY3RpdmVDbGFzc319XG4gICAgICAgID57e3lpZWxkIEBpdGVtIHRvPSdwYWdlJ319PC9hPlxuICAgICAge3svaWZ9fVxuICAgIDwvdWw-XG4gIDwvdGVtcGxhdGU-XG59XG4iXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IjtBQUFBLE9BQU8sZUFBZSxxQkFBcUI7QUFDM0MsU0FBUyxPQUFPLFFBQVEsaUJBQWlCO0FBRXpDLFlBQVksaUJBQWlCLDRCQUE0QjtBQUN6RCxjQUFjLFVBQVUsRUFBRSxJQUFJLFFBQVEsNkJBQTZCO0FBQ25FLFlBQVksbUJBQW1CLGdDQUFnQztBQUUvRCxPQUFPLE1BQU0sZ0JBQWdCO0lBQzNCOzs7O0dBSUMsR0FDRCxTQUFTO0lBQ1Q7UUFDRTs7OztLQUlDLEdBQ0QsY0FBYyxNQUFNOztJQUV0QjtRQUNFOzs7Ozs7Ozs7Ozs7Ozs7Ozs7S0FrQkMsR0FDRCxPQUFPO1FBQ1A7Ozs7Ozs7Ozs7Ozs7Ozs7OztLQWtCQyxHQUNELGFBQWE7OztJQUdmLENBQUMsUUFBUTtJQUFzQixNQUFNLFlBQVk7SUFFakQ7Ozs7O0dBS0MsR0FDRCxNQUFBO1FBQUEsU0FBVSxDQUFBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0VBc0JWLENBQUEsRUFBQTtZQUFBLFdBQUEsSUFBVztZQUFYO2dCQUFBLE9BQUEsS0FBQSxTQUFBLENBQUEsRUFBVztZQUFEO1FBQUE7SUFBQSxDQXRCQTtBQXVCWjtBQUVBLFNBQVMsYUFBYSxJQUFHLE9BQU8sVUFBVSxHQUFHLEtBQUs7SUFDaEQsT0FBTyxXQUFXO0FBQ3BCO0FBRUEsSUFBQSxBQUFNLFFBQU4sTUFBTSxjQUFjO0lBQ2xCO1FBQVEsTUFBTSxPQUFPO1FBQVksY0FBYyxNQUFNOztJQUNyRDtRQUNFLE9BQU87UUFDUCxhQUFhOzs7SUFHZixDQUFDO0lBQWdCLFFBQVEsY0FBYztJQUV2QyxJQUFJLGNBQWM7UUFDaEIsT0FBTyxJQUFJLENBQUMsSUFBSSxDQUFDLFdBQVcsSUFBSTtJQUNsQztJQUVBLFdBQVcsQ0FBQyxVQUFTLE1BQU07UUFDekIsSUFBSSxhQUFZLEtBQUssT0FBTztRQUU1QixPQUFPLElBQUksQ0FBQyxNQUFNLENBQUMsVUFBVSxFQUFFLFdBQVc7SUFDNUMsRUFBRTtJQUVGLE1BQUE7UUFBQSxTQUFVLENBQUE7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7RUFzQlYsQ0FBQSxFQUFBO1lBQUEsV0FBQSxJQUFXO1lBQVg7Z0JBQUEsT0FBQSxLQUFBLFNBQUEsQ0FBQSxFQUFXO1lBQUQ7UUFBQTtJQUFBLENBdEJBO0FBdUJaIn0